/* global axios */
// Integração Wavoip (ligações WhatsApp via webphone). Carrega o SDK UMD do CDN
// uma única vez, registra os tokens da conta e inicia chamadas. Porta do widget
// do AstraChat original (window.wavoip / @wavoip/wavoip-webphone).
const SDK_URL =
  'https://cdn.jsdelivr.net/npm/@wavoip/wavoip-webphone@latest/dist/index.umd.min.js';

let ready = false;
let initInFlight = null;

// ---- histórico: registra a ligação wavoip ao encerrar (p/ o relatório de Ligações) ----
// Saída é rastreada no startCall (temos telefone/direção); entrada tenta ler do evento
// do SDK. Sem telefone não grava (contact_phone é obrigatório no backend).
let currentAccountId = null;
let activeCall = null;

const logWavoipCall = () => {
  const call = activeCall;
  activeCall = null;
  if (!call || !currentAccountId) return;
  const phone = String(call.phone || '').replace(/\D/g, '');
  if (!phone) return;
  const dur = call.answeredAt
    ? Math.max(0, Math.round((Date.now() - call.answeredAt) / 1000))
    : 0;
  axios
    .post(`/api/v1/accounts/${currentAccountId}/kanban/call_history`, {
      contact_phone: phone,
      direction: call.direction || 'OUTGOING',
      status: call.answered ? 'ENDED' : 'NOT_ANSWERED',
      duration_seconds: dur,
      started_at: call.startedAt
        ? new Date(call.startedAt).toISOString()
        : null,
      ended_at: new Date().toISOString(),
      call_method: 'wavoip',
    })
    .catch(() => {});
};

// tenta extrair o telefone do payload do evento do SDK (entrada), com fallbacks.
const evtPhone = evt =>
  (evt &&
    (evt.phone || evt.number || evt.from || evt.tag || evt?.call?.phone)) ||
  '';

const loadScript = () =>
  new Promise((resolve, reject) => {
    if (window.wavoipWebphone) {
      resolve();
      return;
    }
    const s = document.createElement('script');
    s.src = SDK_URL;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });

const fetchTokens = async accountId => {
  const { data } = await axios.get(
    `/api/v1/accounts/${accountId}/kanban/wavoip_tokens/all`
  );
  return data.data || [];
};

const initWavoip = async accountId => {
  if (ready) return true;
  if (initInFlight) return initInFlight;
  initInFlight = (async () => {
    const tokens = await fetchTokens(accountId);
    if (!tokens.length) return false;
    await loadScript();
    const api = await window.wavoipWebphone.render({
      theme: 'system',
      position: 'bottom-right',
      widget: { startOpen: false, showWidgetButton: false },
      callSettings: { displayName: 'Atendimento' },
    });
    tokens.forEach(t => api.device.add(t, true));
    api.on('call:accepted', evt => {
      // entrada: o SDK não passou por startCall → cria o registro a partir do evento
      if (!activeCall) {
        activeCall = {
          phone: evtPhone(evt),
          direction: 'INCOMING',
          startedAt: Date.now(),
        };
      }
      activeCall.answered = true;
      activeCall.answeredAt = Date.now();
      try {
        api.widget.open();
      } catch (e) {
        /* noop */
      }
    });
    api.on('call:ended', () => {
      logWavoipCall();
      try {
        api.widget.close();
      } catch (e) {
        /* noop */
      }
    });
    ready = true;
    return true;
  })();
  try {
    return await initInFlight;
  } finally {
    initInFlight = null;
  }
};

export function useWavoip() {
  const startCall = async (accountId, phone) => {
    const ok = await initWavoip(accountId);
    if (!ok || !window.wavoip) {
      throw new Error('wavoip-not-ready');
    }
    const digits = String(phone || '').replace(/\D/g, '');
    if (!digits) throw new Error('no-phone');
    // rastreia a ligação de SAÍDA p/ registrar no histórico ao encerrar
    currentAccountId = accountId;
    activeCall = {
      phone: digits,
      direction: 'OUTGOING',
      startedAt: Date.now(),
    };
    const devices = window.wavoip.device.get() || [];
    const usable = devices
      .filter(d => d.status === 'open' && d.enable !== false)
      .map(d => d.token);
    try {
      window.wavoip.widget.open();
    } catch (e) {
      /* noop */
    }
    const opts = { displayName: 'Atendimento' };
    if (usable.length) opts.fromTokens = usable;
    return window.wavoip.call.start(digits, opts);
  };

  return { startCall };
}
